<template>
  <div class="stock-snapshot">
    <div class="level snapshot-heading">
      <div class="level-left">
        <div>
          <b-button
              type="is-light"
              icon-pack="fa"
              icon-left="arrow-left"
              class="back-button"
              @click="backToOverview">
            Historische Bestände
          </b-button>
          <h1 class="title">Snapshot #{{ snapshot ? snapshot.id : snapshotId }}</h1>
          <p v-if="snapshot" class="subtitle is-6">
            Lagerbestand vom {{ formatDateTime(snapshot.snapshot_at) }}
          </p>
        </div>
      </div>
      <div v-if="snapshot" class="level-right">
        <b-field grouped>
          <p class="control">
            <b-tag :type="snapshotTypeTag(snapshot.snapshot_type)" size="is-medium">
              {{ snapshotTypeLabel(snapshot.snapshot_type) }}
            </b-tag>
          </p>
          <p class="control">
            <b-tag :type="statusTag(snapshot.status)" size="is-medium">
              {{ statusLabel(snapshot.status) }}
            </b-tag>
          </p>
          <p class="control">
            <b-button
                type="is-primary"
                outlined
                icon-pack="fa"
                icon-left="sync-alt"
                :loading="isLoading"
                @click="fetchData">
              Aktualisieren
            </b-button>
          </p>
        </b-field>
      </div>
    </div>

    <b-loading :active="isLoading" :is-full-page="false" :can-cancel="false" />

    <b-notification v-if="loadError" type="is-danger" has-icon :closable="false">
      <p>{{ loadError }}</p>
      <div class="buttons error-actions">
        <b-button size="is-small" type="is-danger" outlined @click="fetchData">Erneut versuchen</b-button>
        <b-button size="is-small" @click="backToOverview">Zur Übersicht</b-button>
      </div>
    </b-notification>

    <template v-if="snapshot">
      <b-notification
          v-if="snapshot.integrity_valid === false"
          type="is-danger"
          has-icon
          :closable="false"
          class="integrity-warning">
        <strong>Integritätsfehler:</strong>
        Die gespeicherten Snapshot-Daten stimmen nicht mit ihrem Integritätsnachweis überein.
        Die Werte dürfen nicht als verlässlich betrachtet werden.
      </b-notification>

      <b-notification
          v-if="snapshot.status === 'invalid'"
          type="is-warning"
          has-icon
          :closable="false">
        <strong>Dieser Snapshot ist ungültig.</strong>
        {{ snapshot.invalid_reason || 'Es wurde kein Grund angegeben.' }}
      </b-notification>

      <b-message type="is-info" has-icon :closable="false">
        Historische Snapshots sind unveränderlich und können weder bearbeitet noch gelöscht werden.
      </b-message>

      <div class="columns is-multiline metrics">
        <div class="column is-6-tablet is-3-desktop">
          <div class="box metric-box">
            <p class="heading">Gesamtwert</p>
            <p class="title is-4">{{ formatMoney(totalValue) }}</p>
          </div>
        </div>
        <div class="column is-6-tablet is-3-desktop">
          <div class="box metric-box">
            <p class="heading">Positionen</p>
            <p class="title is-4">{{ formatInteger(positionCount) }}</p>
          </div>
        </div>
        <div class="column is-6-tablet is-3-desktop">
          <div class="box metric-box">
            <p class="heading">Periode</p>
            <p class="title is-4">{{ snapshot.period || '–' }}</p>
          </div>
        </div>
        <div class="column is-6-tablet is-3-desktop">
          <div class="box metric-box">
            <p class="heading">Strukturversion</p>
            <p class="title is-4">{{ snapshot.structure_version || document.structure_version || '–' }}</p>
          </div>
        </div>
      </div>

      <div class="columns is-variable is-5 overview-sections">
        <div class="column is-5">
          <section class="box detail-section">
            <h2 class="title is-5">Lagerwerte</h2>
            <div v-if="warehouseEntries.length" class="value-list">
              <div v-for="warehouse in warehouseEntries" :key="warehouse.name" class="value-row">
                <span>{{ warehouse.name }}</span>
                <strong>{{ formatMoney(warehouse.value) }}</strong>
              </div>
            </div>
            <p v-else class="has-text-grey">Keine Lagerwerte vorhanden.</p>
          </section>
        </div>

        <div class="column is-7">
          <section class="box detail-section">
            <h2 class="title is-5">Qualitätsprüfung</h2>
            <div class="quality-grid">
              <div class="quality-item">
                <span>Fehlende Einkaufspreise</span>
                <b-tag :type="qualityValueType(qualityValue('missing_cost_price_count'), true)">
                  {{ formatQualityValue(qualityValue('missing_cost_price_count')) }}
                </b-tag>
              </div>
              <div class="quality-item">
                <span>Negative Bestände</span>
                <b-tag :type="qualityValueType(qualityValue('negative_stock_count'))">
                  {{ formatQualityValue(qualityValue('negative_stock_count')) }}
                </b-tag>
              </div>
              <div class="quality-item">
                <span>Validierungsfehler</span>
                <b-tag :type="qualityValueType(qualityValue('validation_error_count'), true)">
                  {{ formatQualityValue(qualityValue('validation_error_count')) }}
                </b-tag>
              </div>
              <div class="quality-item">
                <span>Warnungen</span>
                <b-tag :type="qualityValueType(qualityValue('warning_count'))">
                  {{ formatQualityValue(qualityValue('warning_count')) }}
                </b-tag>
              </div>
            </div>

            <div v-if="validationErrors.length" class="quality-messages">
              <div class="quality-messages-heading">
                <h3 class="has-text-weight-semibold has-text-danger">
                  {{ validationErrors.length }} Validierungsfehler
                </h3>
                <b-button
                    type="is-danger"
                    outlined
                    size="is-small"
                    icon-pack="fa"
                    :icon-left="validationErrorsExpanded ? 'chevron-up' : 'chevron-down'"
                    :aria-expanded="validationErrorsExpanded ? 'true' : 'false'"
                    aria-controls="snapshot-validation-errors"
                    @click="validationErrorsExpanded = !validationErrorsExpanded">
                  {{ validationErrorsExpanded ? 'Ausblenden' : 'Anzeigen' }}
                </b-button>
              </div>
              <div
                  v-show="validationErrorsExpanded"
                  id="snapshot-validation-errors"
                  class="quality-message-list">
                <div
                    v-for="(error, index) in validationErrors"
                    :key="'error-' + index"
                    class="quality-message is-error">
                  <b-icon pack="fa" icon="times-circle" size="is-small" />
                  <span>{{ formatValidationError(error) }}</span>
                </div>
              </div>
            </div>
            <div v-if="warnings.length" class="quality-messages">
              <div class="quality-messages-heading">
                <h3 class="has-text-weight-semibold has-text-warning-dark">
                  {{ warnings.length }} {{ warnings.length === 1 ? 'Warnung' : 'Warnungen' }}
                </h3>
                <b-button
                    type="is-warning"
                    outlined
                    size="is-small"
                    icon-pack="fa"
                    :icon-left="warningsExpanded ? 'chevron-up' : 'chevron-down'"
                    :aria-expanded="warningsExpanded ? 'true' : 'false'"
                    aria-controls="snapshot-warnings"
                    @click="warningsExpanded = !warningsExpanded">
                  {{ warningsExpanded ? 'Ausblenden' : 'Anzeigen' }}
                </b-button>
              </div>
              <div v-show="warningsExpanded" id="snapshot-warnings" class="quality-message-list">
                <div
                    v-for="(warning, index) in warnings"
                    :key="'warning-' + index"
                    class="quality-message is-warning">
                  <b-icon pack="fa" icon="exclamation-triangle" size="is-small" />
                  <span>{{ formatWarning(warning) }}</span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <section class="box detail-section metadata-section">
        <h2 class="title is-5">Metadaten</h2>
        <dl class="metadata-grid">
          <dt>Snapshot-Zeitpunkt</dt>
          <dd>{{ formatDateTime(snapshot.snapshot_at) }}</dd>
          <dt>Erstellt am</dt>
          <dd>{{ formatDateTime(snapshot.created_at) }}</dd>
          <dt>Typ</dt>
          <dd>{{ snapshotTypeLabel(snapshot.snapshot_type) }}</dd>
          <dt>Ersetzt Snapshot</dt>
          <dd>{{ snapshot.replaces_snapshot_id || '–' }}</dd>
          <dt>Kommentar</dt>
          <dd>{{ snapshot.comment || '–' }}</dd>
          <dt>Integritätsnachweis</dt>
          <dd class="integrity-hash">{{ snapshot.integrity_hash || '–' }}</dd>
        </dl>
      </section>

      <section class="positions-section">
        <div class="level positions-heading">
          <div class="level-left">
            <div>
              <h2 class="title is-4">Bestandspositionen</h2>
              <p class="subtitle is-6">Historischer Inhalt des Snapshot-Dokuments</p>
            </div>
          </div>
          <div class="level-right">
            <b-field label="Einträge je Seite" horizontal>
              <b-select v-model="perPage" size="is-small">
                <option :value="20">20</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
              </b-select>
            </b-field>
          </div>
        </div>

        <b-table
            :data="positions"
            :paginated="positions.length > perPage"
            :per-page="perPage"
            :current-page.sync="currentPage"
            pagination-position="bottom"
            default-sort="article_number"
            default-sort-direction="asc"
            sort-icon="arrow-up"
            sort-icon-size="is-small"
            striped
            hoverable
            aria-next-label="Nächste Seite"
            aria-previous-label="Vorherige Seite"
            aria-page-label="Seite"
            aria-current-label="Aktuelle Seite">

          <b-table-column field="article_number" label="Art.-Nr." sortable searchable v-slot="props">
            <router-link v-if="props.row.article_id" :to="'/articles/' + props.row.article_id" target="_blank">
              {{ props.row.article_number || props.row.article_id }}
            </router-link>
            <span v-else>{{ props.row.article_number || '–' }}</span>
          </b-table-column>

          <b-table-column field="article_name" label="Artikel" sortable searchable v-slot="props">
            {{ props.row.article_name || '–' }}
          </b-table-column>

          <b-table-column field="charge_name" label="Charge" sortable searchable v-slot="props">
            {{ props.row.charge_name || '–' }}
          </b-table-column>

          <b-table-column field="warehouse" label="Lagerplatz" sortable searchable :custom-search="searchStockPlace" v-slot="props">
            <strong>{{ props.row.warehouse || '–' }}</strong>
            <div class="is-size-7 has-text-grey">
              Regal {{ props.row.rack || '–' }} · Fach {{ props.row.shelf || '–' }}
            </div>
          </b-table-column>

          <b-table-column
              field="amount"
              label="Menge"
              numeric
              sortable
              :custom-sort="sortAmount"
              v-slot="props">
            {{ formatQuantity(props.row.amount) }}
          </b-table-column>

          <b-table-column
              field="unit_cost"
              label="Einstandspreis"
              numeric
              sortable
              :custom-sort="sortUnitCost"
              v-slot="props">
            <span v-if="props.row.unit_cost !== null && props.row.unit_cost !== undefined">
              {{ formatMoney(props.row.unit_cost) }}
            </span>
            <b-tag v-else type="is-warning is-light">Fehlt</b-tag>
          </b-table-column>

          <b-table-column
              field="position_value"
              label="Positionswert"
              numeric
              sortable
              :custom-sort="sortPositionValue"
              v-slot="props">
            {{ formatMoney(props.row.position_value) }}
          </b-table-column>

          <b-table-column field="valuation_method" label="Bewertung" centered sortable v-slot="props">
            <b-tag :type="props.row.valuation_method === 'exact' ? 'is-success is-light' : 'is-warning is-light'">
              {{ valuationMethodLabel(props.row.valuation_method) }}
            </b-tag>
          </b-table-column>

          <template #empty>
            <section class="section has-text-centered has-text-grey">
              <b-icon pack="fa" icon="boxes" size="is-large" />
              <p>Dieser Snapshot enthält keine Bestandspositionen.</p>
            </section>
          </template>
        </b-table>
      </section>
    </template>
  </div>
</template>

<script>
import apiservice from '@/services/apiservice'
import {
  compareDecimalStrings,
  formatMoney,
  formatQuantity
} from '@/services/decimal-format'

export default {
  name: 'StockSnapshot',
  data() {
    return {
      snapshot: null,
      isLoading: false,
      loadError: '',
      currentPage: 1,
      perPage: 50,
      validationErrorsExpanded: false,
      warningsExpanded: false
    }
  },
  computed: {
    snapshotId() {
      return this.$route.params.id
    },
    document() {
      return this.snapshot && this.snapshot.snapshot_document
        ? this.snapshot.snapshot_document
        : {}
    },
    summary() {
      return this.document.summary || {}
    },
    positions() {
      return Array.isArray(this.document.positions) ? this.document.positions : []
    },
    totalValue() {
      return this.snapshot.total_value !== null && this.snapshot.total_value !== undefined
        ? this.snapshot.total_value
        : this.summary.total_value
    },
    positionCount() {
      return this.snapshot.position_count !== null && this.snapshot.position_count !== undefined
        ? this.snapshot.position_count
        : this.summary.position_count
    },
    warehouseValues() {
      return this.hasEntries(this.snapshot.warehouse_values)
        ? this.snapshot.warehouse_values
        : this.summary.warehouse_values || {}
    },
    warehouseEntries() {
      return Object.keys(this.warehouseValues).map(name => ({
        name,
        value: this.warehouseValues[name]
      }))
    },
    quality() {
      return this.hasEntries(this.document.quality)
        ? this.document.quality
        : this.snapshot.quality || {}
    },
    validationErrors() {
      return Array.isArray(this.quality.validation_errors) ? this.quality.validation_errors : []
    },
    warnings() {
      return Array.isArray(this.quality.warnings) ? this.quality.warnings : []
    }
  },
  beforeMount() {
    this.fetchData()
  },
  methods: {
    fetchData() {
      this.isLoading = true
      this.loadError = ''

      apiservice()
        .get(`stock/snapshots/${this.snapshotId}`)
        .then(response => {
          this.snapshot = response.data.data
        })
        .catch(error => {
          this.snapshot = null
          this.loadError = this.snapshotErrorMessage(error)
        })
        .then(() => {
          this.isLoading = false
        })
    },
    backToOverview() {
      this.$router.push('/stock/snapshots')
    },
    hasEntries(value) {
      return value && typeof value === 'object' && Object.keys(value).length > 0
    },
    formatMoney,
    formatQuantity,
    formatDateTime(value) {
      if (!value) {
        return '–'
      }
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? value : date.toLocaleString('de-DE')
    },
    formatInteger(value) {
      if (value === null || value === undefined || value === '') {
        return '–'
      }
      return typeof value === 'number' ? value.toLocaleString('de-DE') : value
    },
    snapshotTypeLabel(type) {
      return type === 'monthly' ? 'Monatsabschluss' : type === 'manual' ? 'Manuell' : type || 'Unbekannt'
    },
    snapshotTypeTag(type) {
      return type === 'monthly' ? 'is-info is-light' : 'is-light'
    },
    statusLabel(status) {
      return status === 'creating'
        ? 'Wird erstellt'
        : status === 'completed'
          ? 'Abgeschlossen'
          : status === 'invalid'
            ? 'Ungültig'
            : status || 'Unbekannt'
    },
    statusTag(status) {
      return status === 'completed'
        ? 'is-success'
        : status === 'invalid'
          ? 'is-danger'
          : 'is-info'
    },
    qualityValue(field) {
      const value = this.quality[field]
      return typeof value === 'number' ? value : null
    },
    formatQualityValue(value) {
      return value === null ? '–' : value
    },
    qualityValueType(value, isError) {
      if (value === null) {
        return 'is-light'
      }
      if (value > 0) {
        return isError ? 'is-danger' : 'is-warning'
      }
      return 'is-success is-light'
    },
    formatWarning(warning) {
      if (!warning || typeof warning !== 'object') {
        return 'Unbekannte Auffälligkeit.'
      }

      if (warning.code === 'zero_cost_price') {
        return warning.stock_value_id !== null && warning.stock_value_id !== undefined
          ? `Bewertungsschicht ${warning.stock_value_id} hat einen Einstandspreis von 0,00.`
          : 'Eine Bewertungsschicht hat einen Einstandspreis von 0,00.'
      }
      if (warning.code === 'negative_stock') {
        const count = warning.position_count
        if (count === 1) {
          return '1 Bestandsposition hat einen negativen Bestand.'
        }
        if (count !== null && count !== undefined) {
          return `${count} Bestandspositionen haben einen negativen Bestand.`
        }
        return 'Bestandspositionen haben einen negativen Bestand.'
      }

      return `Unbekannte Auffälligkeit: ${warning.code || 'unbekannter Code'}`
    },
    formatValidationError(error) {
      if (typeof error === 'string') {
        return error
      }
      if (error && typeof error === 'object') {
        return error.message || error.reason || `Validierungsfehler: ${error.code || 'unbekannter Code'}`
      }
      return 'Unbekannter Validierungsfehler.'
    },
    valuationMethodLabel(method) {
      return method === 'exact' ? 'Exakt' : method === 'simplified' ? 'Vereinfacht' : method || 'Unbekannt'
    },
    searchStockPlace(row, input) {
      return [row.warehouse, row.rack, row.shelf]
        .filter(Boolean)
        .join('|')
        .toLowerCase()
        .includes(input.toLowerCase())
    },
    sortAmount(first, second, isAsc) {
      return this.sortDecimalRows(first.amount, second.amount, isAsc)
    },
    sortUnitCost(first, second, isAsc) {
      return this.sortDecimalRows(first.unit_cost, second.unit_cost, isAsc)
    },
    sortPositionValue(first, second, isAsc) {
      return this.sortDecimalRows(first.position_value, second.position_value, isAsc)
    },
    sortDecimalRows(first, second, isAsc) {
      if (first === null || first === undefined) {
        return second === null || second === undefined ? 0 : 1
      }
      if (second === null || second === undefined) {
        return -1
      }
      const result = compareDecimalStrings(first, second)
      return isAsc ? result : -result
    },
    snapshotErrorMessage(error) {
      if (error && (error.http_status === 404 || error.status_code === 404)) {
        return `Snapshot #${this.snapshotId} wurde nicht gefunden.`
      }
      if (error && typeof error.message === 'string') {
        return error.message
      }
      return `Snapshot #${this.snapshotId} konnte nicht geladen werden.`
    }
  }
}
</script>

<style scoped>
.snapshot-heading {
  align-items: flex-end;
  margin-bottom: 1.5rem;
}

.back-button {
  margin-bottom: 1rem;
}

.snapshot-heading .subtitle,
.positions-heading .subtitle {
  margin-top: -0.75rem;
}

.error-actions {
  margin-top: 0.75rem;
}

.integrity-warning {
  border: 2px solid #f14668;
}

.metrics {
  margin-top: 0.5rem;
}

.metric-box {
  height: 100%;
}

.metric-box .title {
  overflow-wrap: anywhere;
}

.detail-section {
  height: 100%;
}

.value-list {
  display: grid;
  gap: 0.25rem;
}

.value-row,
.quality-item {
  align-items: center;
  border-bottom: 1px solid #ededed;
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(0, 1fr) auto;
  padding: 0.5rem 0;
}

.quality-grid {
  display: grid;
  gap: 0 1.5rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.quality-messages {
  margin-top: 1rem;
}

.quality-messages-heading {
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
}

.quality-message-list {
  margin-top: 0.5rem;
}

.quality-message {
  align-items: flex-start;
  border-radius: 4px;
  display: flex;
  gap: 0.65rem;
  margin-top: 0.5rem;
  padding: 0.65rem 0.75rem;
}

.quality-message.is-error {
  background-color: #feecf0;
  color: #cc0f35;
}

.quality-message.is-warning {
  background-color: #fffaeb;
  color: #946c00;
}

.metadata-section {
  margin-bottom: 2rem;
}

.metadata-grid {
  display: grid;
  gap: 0.65rem 1.5rem;
  grid-template-columns: minmax(10rem, 0.35fr) minmax(0, 1fr);
}

.metadata-grid dt {
  color: #7a7a7a;
}

.metadata-grid dd {
  margin: 0;
}

.integrity-hash {
  font-family: monospace;
  overflow-wrap: anywhere;
}

.positions-heading {
  align-items: flex-end;
}

@media screen and (max-width: 768px) {
  .snapshot-heading,
  .positions-heading {
    display: block;
  }

  .snapshot-heading .level-right,
  .positions-heading .level-right {
    margin-top: 1rem;
  }

  .quality-grid {
    grid-template-columns: 1fr;
  }

  .metadata-grid {
    grid-template-columns: 1fr;
  }

  .metadata-grid dd:not(:last-child) {
    margin-bottom: 0.5rem;
  }
}
</style>
